import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { displayName, initials, toPercent } from './pickupTrackingLogic';
import type { SortDir, VehicleFilter } from './pickupTrackingLogic';
import { ALERT_COLOR, VEHICLE_THEME, VEHICLE_TYPES, vehicleThemeOf } from './pickupTrackingTheme';
import type { TrackingDriver, TrackingSortKey } from './pickupTrackingTypes';
import styles from './PickupDriverTrackingPage.module.css';

const SORT_KEYS: TrackingSortKey[] = ['rate', 'vol'];
const FILTERS: VehicleFilter[] = ['all', ...VEHICLE_TYPES];

interface TrackingDriverListProps {
    /** 已按筛选 + 排序处理好的列表 */
    drivers: TrackingDriver[];
    vehicleCounts: Record<VehicleFilter, number>;
    alertCount: number;
    vehicle: VehicleFilter;
    alertOnly: boolean;
    sortKey: TrackingSortKey;
    sortDir: SortDir;
    selectedKey: string | null;
    onVehicleChange: (v: VehicleFilter) => void;
    onSortClick: (k: TrackingSortKey) => void;
    onToggleAlert: () => void;
    onFlipDir: () => void;
    onSelect: (d: TrackingDriver) => void;
}

export const TrackingDriverList: React.FC<TrackingDriverListProps> = ({
    drivers,
    vehicleCounts,
    alertCount,
    vehicle,
    alertOnly,
    sortKey,
    sortDir,
    selectedKey,
    onVehicleChange,
    onSortClick,
    onToggleAlert,
    onFlipDir,
    onSelect,
}) => {
    const { t } = useTranslation();
    const listRef = useRef<HTMLDivElement>(null);

    // 选中的行不在可视区时滚到 1/3 处（原型 searchPick / 地图点车都会走这里）
    useEffect(() => {
        const box = listRef.current;
        if (!box || !selectedKey) return;
        const row = Array.from(box.children).find((el) => (el as HTMLElement).dataset.k === selectedKey) as
            | HTMLElement
            | undefined;
        if (!row) return;
        if (row.offsetTop < box.scrollTop || row.offsetTop + row.offsetHeight > box.scrollTop + box.clientHeight) {
            box.scrollTo({ top: Math.max(0, row.offsetTop - box.clientHeight / 3), behavior: 'smooth' });
        }
    }, [selectedKey]);

    return (
        <div className={`${styles.side} ${styles.panel}`}>
            <div className={styles.phead}>
                <span className={styles.dia} />
                <h2 className={styles.pheadTitle}>{t('pickup.driverTracking.listTitle')}</h2>
                <span className={styles.pheadHint}>
                    {t('pickup.driverTracking.driverCount', { count: drivers.length })}
                </span>
            </div>
            <div className={styles.sideFilters}>
                <div className={styles.chipRow}>
                    {FILTERS.map((k) => (
                        <button
                            key={k}
                            type="button"
                            className={`${styles.chip} ${vehicle === k ? styles.chipOn : ''}`}
                            onClick={(e) => {
                                e.stopPropagation();
                                onVehicleChange(k);
                            }}
                        >
                            {k !== 'all' && <i style={{ background: VEHICLE_THEME[k].color }} />}
                            {t(k === 'all' ? 'pickup.driverTracking.all' : `pickup.driverTracking.vehicle.${k}`)}{' '}
                            <b>{vehicleCounts[k]}</b>
                        </button>
                    ))}
                </div>
                <div className={styles.sortRow}>
                    <span className={styles.sortLabel}>{t('pickup.driverTracking.sortBy')}</span>
                    <div className={styles.chipRow}>
                        {SORT_KEYS.map((k) => (
                            <button
                                key={k}
                                type="button"
                                className={`${styles.chip} ${sortKey === k ? styles.chipOn : ''}`}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onSortClick(k);
                                }}
                            >
                                {t(`pickup.driverTracking.sort.${k}`)}
                            </button>
                        ))}
                    </div>
                    <button
                        type="button"
                        className={`${styles.chip} ${styles.chipAlert} ${alertOnly ? styles.chipOn : ''}`}
                        style={{ marginLeft: 'auto' }}
                        onClick={(e) => {
                            e.stopPropagation();
                            onToggleAlert();
                        }}
                    >
                        <i style={{ background: ALERT_COLOR }} />
                        {t('pickup.driverTracking.alertOnly')} <b>{alertCount}</b>
                    </button>
                    <button
                        type="button"
                        className={styles.dirbtn}
                        style={{ marginLeft: 0 }}
                        title={t('pickup.driverTracking.sortDir') || ''}
                        aria-label={t('pickup.driverTracking.sortDir') || ''}
                        onClick={(e) => {
                            e.stopPropagation();
                            onFlipDir();
                        }}
                    >
                        {sortDir === -1 ? '▼' : '▲'}
                    </button>
                </div>
            </div>
            <div className={styles.dlist} ref={listRef}>
                {drivers.map((d) => {
                    const color = vehicleThemeOf(d.vehicleType).color;
                    const name = displayName(d);
                    return (
                        <button
                            key={d.key}
                            type="button"
                            data-k={d.key}
                            data-driver-id={d.key}
                            className={`${styles.row} ${selectedKey === d.key ? styles.rowOn : ''}`}
                            onClick={(e) => {
                                e.stopPropagation();
                                onSelect(d);
                            }}
                        >
                            <div
                                className={styles.av}
                                style={{
                                    color,
                                    // 原型头像有淡淡的外发光，不是荧光棒 —— 3px + 半透明足够（同派送大屏）
                                    filter: `drop-shadow(0 0 3px ${color}80)`,
                                }}
                            >
                                {initials(d.name === d.id ? null : d.name, d.id)}
                            </div>
                            <div className={styles.rowMain}>
                                <div className={styles.rowTop}>
                                    <b className={styles.rowName}>{name}</b>
                                    {d.vehicleType && (
                                        <span className={styles.vt} style={{ color }}>
                                            {t(`pickup.driverTracking.vehicle.${d.vehicleType}`)}
                                        </span>
                                    )}
                                </div>
                                <div className={styles.rowProg}>
                                    <div className={styles.bar}>
                                        <i style={{ width: `${toPercent(d.ok, d.total)}%` }} />
                                        <s style={{ width: `${toPercent(d.fail, d.total)}%` }} />
                                    </div>
                                    <span className={styles.rowPct} title={t('pickup.driverTracking.sort.rate') || ''}>
                                        {toPercent(d.ok + d.fail, d.total)}%
                                    </span>
                                </div>
                                {d.anomalies.length > 0 && (
                                    <div className={styles.rowTags}>
                                        {d.anomalies.map((a) => (
                                            <span key={a} className={styles.atag}>
                                                {t(`pickup.driverTracking.anomaly.${a}`)}
                                            </span>
                                        ))}
                                    </div>
                                )}
                            </div>
                            <div className={styles.rowRight}>
                                <b className={styles.rowOk}>
                                    {d.ok}
                                    <em>/{d.total}</em>
                                </b>
                                <em
                                    className={styles.rowFail}
                                    style={{ color: d.fail ? '#f87171' : '#5f9ac9' }}
                                >
                                    {d.fail
                                        ? t('pickup.driverTracking.rowFail', { n: d.fail })
                                        : t('pickup.driverTracking.rowDone', { n: d.ok + d.fail, total: d.total })}
                                </em>
                            </div>
                        </button>
                    );
                })}
            </div>
        </div>
    );
};
